import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Huggg } from 'components';
import VoucherTypes from './Types/Types';
import dictionary from 'config/dictionary';
import styles from './voucher.module.scss';
import { SendQRCodeButton } from 'components/ui';
import {
  getRedeemedSegmentImage,
  getRedeemedSegmentName,
  shouldUseRedeemedSegmentInformation,
} from 'utilities/huggg';
import { format, parseISO } from 'date-fns';
import { interpolate } from 'utilities/interpolate';
import SuccessIcon from 'components/SuccessIcon/SuccessIcon';
import classNames from 'classnames';

const VoucherClaimed = ({
  brand,
  huggg,
  vouchers,
  voucherExpiry,
  redeemedSegment,
  hideProductImage,
  isFormRedemptionType,
  isPin4Type,
  isPrepaidCardType,
  isBankPaymentType,
  isBlueHuggg,
  onSendQRCodeEmail,
  hasFulfilledVoucher,
  isVirginExperience,
  onFulfil,
  isGenerating,
}) => {
  const redeemedSegmentImage = shouldUseRedeemedSegmentInformation(huggg)
    ? getRedeemedSegmentImage(huggg)
    : null;

  const redeemedSegmentName = shouldUseRedeemedSegmentInformation(huggg)
    ? getRedeemedSegmentName(huggg)
    : null;

  const formattedVoucherExpiry = voucherExpiry
    ? format(parseISO(voucherExpiry), 'do MMM yyyy')
    : null;

  const shouldShowSuccessMessaging = redeemedSegment?.voucherType === 'tillo';
  const isFulfilledVirginExperience = isVirginExperience && hasFulfilledVoucher;
  const shouldShowVirginSuccessMessaging = isFulfilledVirginExperience;

  // Derive bank-payment from the voucher response itself rather than relying
  // on the `selectedSegment` Redux flag — selectedSegment may be empty on a
  // post-redemption refresh, whereas redeemedSegment + the voucher data items
  // are sourced from the claim response and always reflect reality.
  const isBankPayment =
    isBankPaymentType ||
    redeemedSegment?.voucherType === 'bank-payment' ||
    Boolean(vouchers?.some((item) => item.type === 'providerPaymentId'));

  const shouldShowVoucherExpiry =
    Boolean(formattedVoucherExpiry) &&
    !isFormRedemptionType &&
    !isPin4Type &&
    !isPrepaidCardType &&
    !isBankPayment &&
    !isFulfilledVirginExperience;

  const showSendQRCodeButton =
    vouchers?.length && vouchers.some((item) => item.type === 'qr');

  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        {shouldShowSuccessMessaging && (
          <div className={styles.successIconWrapper}>
            <div className={styles.successIcon}>
              <SuccessIcon alt="Success" scale={1} />
            </div>
            <p>
              {interpolate(
                isBlueHuggg
                  ? dictionary.voucherClaimed
                  : dictionary.giftCardClaimed,
              )}
            </p>
          </div>
        )}
        <Huggg
          huggg={huggg}
          hideImage={hideProductImage}
          imageOverride={redeemedSegmentImage}
          titleOverride={redeemedSegmentName}
          hideDescription={Boolean(redeemedSegmentName)}
        />
      </div>
      {shouldShowVirginSuccessMessaging && (
        <div
          className={classNames(
            styles.successIconWrapper,
            isFulfilledVirginExperience ? styles.virginExperience : '',
          )}
        >
          <div className={styles.successIcon}>
            <SuccessIcon alt="Success" scale={1} />
          </div>
        </div>
      )}
      <VoucherTypes
        brandName={brand.name}
        vouchers={vouchers}
        redeemedSegment={redeemedSegment}
        data-test-id="voucher-image"
        isBlueHuggg={isBlueHuggg}
        huggg={huggg}
        isVirginExperience={isVirginExperience}
        onFulfil={onFulfil}
        isGenerating={isGenerating}
      />
      {shouldShowVirginSuccessMessaging && (
        <p className={styles.successMessage}>
          {dictionary.virginExperiences.successMessage}
        </p>
      )}
      {shouldShowVoucherExpiry && (
        <p className={styles.voucherExpiry}>
          {interpolate(
            dictionary.voucherExpiry,
            '[expiry_date]',
            <>{formattedVoucherExpiry}</>,
          )}
        </p>
      )}

      {showSendQRCodeButton && (
        <div className={styles.buttonContainer}>
          <SendQRCodeButton
            hugggId={huggg.id}
            onSendQRCodeEmail={onSendQRCodeEmail}
          />
        </div>
      )}
    </>
  );
};

VoucherClaimed.propTypes = {
  brand: PropTypes.object.isRequired,
  huggg: PropTypes.object.isRequired,
  vouchers: PropTypes.array,
  voucherExpiry: PropTypes.string,
  isFormRedemptionType: PropTypes.bool,
  isPin4Type: PropTypes.bool,
  isPrepaidCardType: PropTypes.bool,
  isBankPaymentType: PropTypes.bool,
  redeemedSegment: PropTypes.object,
  hideProductImage: PropTypes.bool,
  onSendQRCodeEmail: PropTypes.func,
  isBlueHuggg: PropTypes.bool,
  hasFulfilledVoucher: PropTypes.bool,
  isVirginExperience: PropTypes.bool,
  onFulfil: PropTypes.func,
  isGenerating: PropTypes.bool,
};

export default VoucherClaimed;
